import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const navItems = [
    { key: 'home', label: t('home'), id: 'home' },
    { key: 'services', label: t('services'), id: 'services' },
    { key: 'solutions', label: t('solutions'), id: 'solutions' },
    { key: 'about', label: t('about'), id: 'about' },
    { key: 'contact', label: t('contact'), id: 'contact' }
  ];

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.isExternal && item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
      setIsMenuOpen(false);
    } else if (item.id) {
      scrollToSection(item.id);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/e20f1140862a90922d0fa0db5fb08a3f/188a4d334a92983eeb45c3f8b0720b97.png" 
              alt="RAM LIMITED - Excellence in Every Detail" 
              className="h-12 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => handleNavClick(item)}
                className="text-gray-700 hover:text-[#005776] font-medium transition-colors cursor-pointer"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Language Switcher & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#005776] transition-colors cursor-pointer"
            >
              <i className="ri-global-line"></i>
              <span>{i18n.language === 'en' ? 'العربية' : 'English'}</span>
            </button>
            <Button 
              onClick={() => window.open('https://store.almukhatat.sa/', '_blank', 'noopener,noreferrer')}
              variant="secondary"
            >
              <i className="ri-store-line mr-2"></i>
              {t('onlineStore')}
            </Button>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-[#FE5000] hover:bg-[#E04800]"
            >
              <i className="ri-phone-line mr-2"></i>
              {t('hero.getFreeQuote')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-gray-700 hover:text-[#005776] cursor-pointer"
          >
            <i className={isMenuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 py-4">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleNavClick(item)}
                  className="text-left text-gray-700 hover:text-[#005776] font-medium transition-colors cursor-pointer"
                >
                  {item.label}
                </button>
              ))}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-[#005776] transition-colors cursor-pointer"
                >
                  <i className="ri-global-line"></i>
                  <span>{i18n.language === 'en' ? 'العربية' : 'English'}</span>
                </button>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => window.open('https://store.almukhatat.sa/', '_blank', 'noopener,noreferrer')}
                    size="sm"
                    variant="secondary"
                  >
                    {t('onlineStore')}
                  </Button>
                  <Button 
                    onClick={() => scrollToSection('contact')}
                    size="sm"
                    className="bg-[#FE5000] hover:bg-[#E04800]"
                  >
                    {t('hero.getFreeQuote')}
                  </Button>
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
